import { createContext, useContext, ReactNode, useState, useMemo } from 'react';
import { Employee } from 'src/models/employee';

interface EmployeeContextProps {
  employee: Employee;
  setEmployee: (employee: Employee) => void;
}

// Provide a default value to avoid runtime errors
const defaultEmployee: EmployeeContextProps = {
  employee: {
    EmployeeId: '',
    Name: '',
    Email: '',
    Role: 0,
    SubjectCount: 0,
    Active: false,
    Academy: '',
  },
  setEmployee: () => {
    throw new Error(
      'setEmployee function must be overridden by EmployeeProvider'
    );
  },
};

export const EmployeeContext =
  createContext<EmployeeContextProps>(defaultEmployee);

export const useEmployee = () => {
  const context = useContext(EmployeeContext);
  if (!context) {
    throw new Error(
      'useEmployee must be used within an EmployeeProvider. Ensure EmployeeProvider wraps your component tree.'
    );
  }
  return context;
};

export const EmployeeProvider = ({ children }: { children: ReactNode }) => {
  const [employee, setEmployee] = useState<Employee>(defaultEmployee.employee);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({ employee, setEmployee }), [employee]);

  return (
    <EmployeeContext.Provider value={value}>
      {children}
    </EmployeeContext.Provider>
  );
};
